import { FC } from 'react';

import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency, formatNumber } from 'utils/formats';

import { IncentiveOffer } from 'modules/incentivesMarket/types';

type IncentiveOfferSellerLimitsBannerProps = {
  offer: IncentiveOffer;
  totalUnits?: number;
  sellableUnits?: number;
};

const IncentiveOfferSellerLimitsBanner: FC<IncentiveOfferSellerLimitsBannerProps> = ({
  offer,
  totalUnits,
  sellableUnits,
}) => {
  const { t } = useTranslate();
  const limits = offer.sellerParticipationLimits;

  if (offer.type !== 'BUY' || !limits || limits.allowSellAll !== false) {
    return null;
  }

  const rules: string[] = [];

  if (limits.maxPercentUnvested !== undefined && limits.maxPercentUnvested < 100) {
    rules.push(
      t('IncentivesMarketSellerLimitMaxPercentAvailable', {
        percent: formatNumber(limits.maxPercentUnvested),
      })
    );
  }

  if (limits.maxAmount && limits.maxAmount > 0) {
    rules.push(
      t('IncentivesMarketSellerLimitMaxAmount', {
        amount: formatCurrency(limits.maxAmount),
      })
    );
  }

  if (limits.maxStocks && limits.maxStocks > 0) {
    rules.push(
      t('IncentivesMarketSellerLimitMaxStockUnits', {
        units: formatNumber(limits.maxStocks),
      })
    );
  }

  if (limits.maxPercentStocks && limits.maxPercentStocks > 0) {
    rules.push(
      t('IncentivesMarketSellerLimitMaxStockPercent', {
        percent: formatNumber(limits.maxPercentStocks),
      })
    );
  }

  if (limits.maxPhantoms && limits.maxPhantoms > 0) {
    rules.push(
      t('IncentivesMarketSellerLimitMaxPhantomUnits', {
        units: formatNumber(limits.maxPhantoms),
      })
    );
  }

  if (limits.maxPercentPhantoms && limits.maxPercentPhantoms > 0) {
    rules.push(
      t('IncentivesMarketSellerLimitMaxPhantomPercent', {
        percent: formatNumber(limits.maxPercentPhantoms),
      })
    );
  }

  if (!rules.length) {
    rules.push(t('IncentivesMarketSellerLimitsCustom'));
  }

  const showAvailableToSell =
    totalUnits !== undefined &&
    totalUnits > 0 &&
    sellableUnits !== undefined;

  return (
    <div className="alert alert-pro alert-info mb-4">
      <div className="alert-text">
        <h6 className="alert-heading fs-13px mb-2">
          {t('IncentivesMarketSellerLimitsBannerTitle')}
        </h6>
        <ul className="list list-sm mb-0">
          {rules.map((rule) => (
            <li key={rule} className="list-item pb-1 fs-13px">
              {rule}
            </li>
          ))}
        </ul>
        {showAvailableToSell && (
          <p className="mb-0 mt-2 fs-13px">
            {t('IncentivesMarketSellerLimitAvailableToSell', {
              total: formatNumber(totalUnits),
              available: formatNumber(sellableUnits),
            })}
          </p>
        )}
      </div>
    </div>
  );
};

export default IncentiveOfferSellerLimitsBanner;
